import React from 'react';
import { Check, Infinity as InfinityIcon, Minus } from 'lucide-react';
import { TIERS, COMPARE_ROWS } from '../mock/pricingData';
import { useCurrency } from '../context/CurrencyContext';

const CompareTiers = ({ selected }) => {
  const { formatMoney } = useCurrency();

  const formatCell = (row, tier) => {
    if (row.value) return row.value;
    if (row.check === 'freeTrial') {
      return tier.freeTrial ? (
        <Check className="w-4 h-4 text-emerald-400 mx-auto" />
      ) : (
        <Minus className="w-3 h-3 text-white/30 mx-auto" />
      );
    }
    if (row.check) {
      return (
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-400/15 border border-emerald-400/40">
          <Check className="w-3.5 h-3.5 text-emerald-300" strokeWidth={3} />
        </span>
      );
    }
    const v = tier[row.key];
    if (v === 'Unlimited') {
      return (
        <span className="inline-flex items-center gap-1 text-emerald-300">
          <InfinityIcon className="w-3.5 h-3.5" />
          Unlimited
        </span>
      );
    }
    if (row.key === 'priceLabel') {
      if (tier.id === 'enterprise') return 'Custom';
      return `${formatMoney(tier.price)}${row.suffix || ''}`;
    }
    if (row.key === 'additionalCreatorPrice') {
      if (v === 'Custom') return 'Custom';
      return `${formatMoney(v)}/mo`;
    }
    if (row.key === 'maxTags' && typeof v === 'number') {
      return v.toLocaleString('en-US');
    }
    return v;
  };

  const rateCell = (label) => {
    // Convert prefixed rate labels e.g. "$20/user/mo" → currency-formatted
    if (label === '$20/user/mo') return `${formatMoney(20)}/user/mo`;
    if (label === '$50/app/mo') return `${formatMoney(50)}/app/mo`;
    if (label === '$10/pack/mo') return `${formatMoney(10)}/pack/mo`;
    return label;
  };

  return (
    <section className="mt-16 md:mt-24">
      <div className="text-[11px] tracking-[0.22em] font-semibold uppercase text-emerald-300">
        Side-by-side
      </div>
      <h2 className="mt-2 text-3xl md:text-4xl font-semibold text-white tracking-tight">
        Compare all four tiers
      </h2>
      <p className="mt-3 max-w-2xl text-white/60 leading-relaxed">
        Every tier includes the same view-only add-on rate. Viewers execute entirely on-premise,
        generating zero cloud LLM token overhead.
      </p>

      <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-6 py-5 text-[10px] tracking-[0.2em] font-semibold uppercase text-white/50 w-[26%]">
                  Capability
                </th>
                {TIERS.map((t) => {
                  const isSel = selected === t.id;
                  return (
                    <th
                      key={t.id}
                      className={`text-left px-5 py-5 ${isSel ? 'bg-emerald-400/[0.06]' : ''}`}
                    >
                      <div className={`text-[10px] tracking-[0.2em] font-semibold uppercase ${isSel ? 'text-emerald-300' : 'text-white/50'}`}>
                        {t.label}
                      </div>
                      <div className={`mt-2 text-lg font-semibold ${isSel ? 'text-emerald-300' : 'text-white'}`}>
                        {t.name.replace('Itanta ', '')}
                      </div>
                      <div className="mt-1 text-xs text-white/50">
                        {t.id === 'enterprise' ? 'Custom' : `${formatMoney(t.price)}/mo`}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row, i) => (
                <tr key={i} className={`border-b border-white/5 ${i % 2 === 1 ? 'bg-white/[0.015]' : ''}`}>
                  <td className="px-6 py-4 text-white/70">{row.label}</td>
                  {TIERS.map((t) => {
                    const isSel = selected === t.id;
                    let cell = formatCell(row, t);
                    if (row.value) cell = rateCell(row.value);
                    return (
                      <td key={t.id} className={`px-5 py-4 text-white/85 ${isSel ? 'bg-emerald-400/[0.04]' : ''}`}>
                        {cell}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default CompareTiers;
